package com.example.positionahead.domain

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.PositionSnapshot
import com.example.positionahead.model.Quote
import com.example.positionahead.model.RebalanceTrade
import com.example.positionahead.model.TargetHolding
import com.example.positionahead.model.effectiveBondTarget
import com.example.positionahead.model.effectiveStockTarget
import kotlin.math.abs

object RebalancePlanner {

    fun buildTargetHoldings(summary: PortfolioSummary, goal: FinancialGoal): List<TargetHolding> {
        val total = summary.totalMarketValue
        if (total <= 0 || summary.positions.isEmpty()) return emptyList()

        val maxWeight = goal.maxSinglePositionPercent
        val stockBucket = goal.effectiveStockTarget().toDouble()
        val bondBucket = goal.effectiveBondTarget().toDouble()

        val equities = summary.positions.filter { isEquityLike(it) && (it.marketValue > 0) }
        val bonds = summary.positions.filter { it.position.assetClass == AssetClass.BOND && it.marketValue > 0 }
        val equityValue = equities.sumOf { it.marketValue }.coerceAtLeast(1.0)
        val bondValue = bonds.sumOf { it.marketValue }.coerceAtLeast(1.0)

        val targets = mutableListOf<TargetHolding>()

        equities.forEach { snapshot ->
            val symbol = snapshot.position.symbol.uppercase()
            val shareOfBucket = snapshot.marketValue / equityValue
            val weight = (stockBucket * shareOfBucket).coerceAtMost(maxWeight)
            targets += TargetHolding(symbol, weight, total * weight / 100.0)
        }
        bonds.forEach { snapshot ->
            val symbol = snapshot.position.symbol.uppercase()
            val shareOfBucket = snapshot.marketValue / bondValue
            val weight = (bondBucket * shareOfBucket).coerceAtMost(maxWeight)
            targets += TargetHolding(symbol, weight, total * weight / 100.0)
        }

        // Normalize so targets sum to ~100% of portfolio
        val sumWeight = targets.sumOf { it.targetWeightPercent }
        if (sumWeight > 0) {
            return targets.map {
                val normalized = it.targetWeightPercent * 100.0 / sumWeight
                it.copy(
                    targetWeightPercent = normalized,
                    targetMarketValue = total * normalized / 100.0,
                )
            }.sortedByDescending { it.targetMarketValue }
        }

        return targets.sortedByDescending { it.targetMarketValue }
    }

    fun computeTrades(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        quotes: Map<String, Quote>,
    ): List<RebalanceTrade> {
        val total = summary.totalMarketValue
        if (total <= 0 || summary.positions.isEmpty()) return emptyList()

        val trades = mutableListOf<RebalanceTrade>()
        val owned = summary.positions.associateBy { it.position.symbol.uppercase() }

        // Concentration: trim to max single position
        summary.positions
            .filter { it.hasLivePrice && it.portfolioWeightPercent > goal.maxSinglePositionPercent }
            .forEach { snapshot ->
                val excessWeight = snapshot.portfolioWeightPercent - goal.maxSinglePositionPercent
                val sellAmount = total * excessWeight / 100.0
                if (sellAmount >= 25.0) {
                    val buySymbol = pickBuySymbol(summary, goal, quotes, exclude = snapshot.position.symbol)
                    trades += RebalanceTrade(
                        sellSymbol = snapshot.position.symbol.uppercase(),
                        sellAmountDollars = sellAmount,
                        buySymbol = buySymbol,
                        buyAmountDollars = sellAmount,
                        reason = "Reduce ${snapshot.position.symbol} from ${snapshot.portfolioWeightPercent.format(1)}% " +
                            "to about ${goal.maxSinglePositionPercent.format(0)}% max weight",
                    )
                }
            }

        // Asset allocation drift
        val targetStock = goal.effectiveStockTarget().toDouble()
        val stockDrift = summary.stockAllocationPercent - targetStock
        if (abs(stockDrift) > goal.rebalanceThresholdPercent) {
            val shiftAmount = total * abs(stockDrift) / 100.0
            if (shiftAmount >= 25.0) {
                if (stockDrift > 0) {
                    val trim = summary.positions
                        .filter { isEquityLike(it) && it.hasLivePrice }
                        .maxByOrNull { it.portfolioWeightPercent }
                    if (trim != null) {
                        val buySymbol = pickBuySymbol(summary, goal, quotes, preferBond = true, exclude = trim.position.symbol)
                        trades += RebalanceTrade(
                            sellSymbol = trim.position.symbol.uppercase(),
                            sellAmountDollars = shiftAmount,
                            buySymbol = buySymbol,
                            buyAmountDollars = shiftAmount,
                            reason = "Stocks at ${summary.stockAllocationPercent.format(1)}% vs ${targetStock.format(0)}% target",
                        )
                    }
                } else {
                    val buySymbol = pickBuySymbol(summary, goal, quotes, preferEquity = true)
                    val fundFrom = summary.positions
                        .filter { it.position.assetClass == AssetClass.BOND && it.hasLivePrice }
                        .maxByOrNull { it.marketValue }
                        ?: summary.positions.filter { it.hasLivePrice }.maxByOrNull { it.marketValue }
                    if (buySymbol != null && fundFrom != null) {
                        trades += RebalanceTrade(
                            sellSymbol = fundFrom.position.symbol.uppercase(),
                            sellAmountDollars = shiftAmount.coerceAtMost(fundFrom.marketValue * 0.5),
                            buySymbol = buySymbol,
                            buyAmountDollars = shiftAmount.coerceAtMost(fundFrom.marketValue * 0.5),
                            reason = "Stocks at ${summary.stockAllocationPercent.format(1)}% vs ${targetStock.format(0)}% target",
                        )
                    }
                }
            }
        }

        return trades
            .distinctBy { "${it.sellSymbol}-${it.buySymbol}-${it.sellAmountDollars.toInt()}" }
            .take(6)
    }

    fun deltaFromCurrent(summary: PortfolioSummary, targets: List<TargetHolding>): List<Pair<String, Double>> {
        val owned = summary.positions.associateBy { it.position.symbol.uppercase() }
        return targets.mapNotNull { target ->
            val current = owned[target.symbol]?.marketValue ?: 0.0
            val delta = target.targetMarketValue - current
            if (abs(delta) < 1.0) null else target.symbol to delta
        }
    }

    private fun pickBuySymbol(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        quotes: Map<String, Quote>,
        preferBond: Boolean = false,
        preferEquity: Boolean = false,
        exclude: String? = null,
    ): String? {
        val owned = summary.positions.map { it.position.symbol.uppercase() }.toSet()
        val candidates = when {
            preferBond -> listOf("BND", "AGG", "BIV")
            preferEquity -> MarketSuggestionCatalog.candidateSymbolsFor(goal.objective)
            else -> MarketSuggestionCatalog.candidateSymbolsFor(goal.objective)
        }
        return candidates
            .map { it.uppercase() }
            .firstOrNull { it !in owned && it != exclude?.uppercase() && (quotes[it]?.price ?: 0.0) > 0 }
            ?: candidates.firstOrNull { it.uppercase() != exclude?.uppercase() }
    }

    private fun isEquityLike(snapshot: PositionSnapshot): Boolean =
        when (snapshot.position.assetClass) {
            AssetClass.STOCK, AssetClass.ETF, AssetClass.REIT -> true
            AssetClass.BOND, AssetClass.CASH -> false
        }

    private fun Double.format(decimals: Int): String {
        val factor = 10.0.pow(decimals)
        return (kotlin.math.round(this * factor) / factor).toString()
    }

    private fun Double.pow(n: Int): Double {
        var result = 1.0
        repeat(n) { result *= 10.0 }
        return result
    }
}
